import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Mic, ShieldCheck, Activity, Server, FileText, ArrowRight, Radio, Lock, Play, ArrowDown, ArrowUp, Cloud, Smartphone, Wrench, CalendarClock, Menu, X } from "lucide-react";
import { toast } from "sonner";
import { Logo } from "../components/Logo";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { IndiaBranchMap } from "../components/IndiaBranchMap";
import { LanguageSelector } from "../components/LanguageSelector";
import { ThemeToggle } from "../components/ThemeToggle";
import { StickyBookDemo } from "../components/StickyBookDemo";
import { ChatWidget } from "../components/ChatWidget";
import { BookDemoModal } from "../components/BookDemoModal";
import { BookDemoProvider, useBookDemo } from "../context/BookDemoContext";
import { useLanguage } from "../context/LanguageContext";
import { useApplyTheme } from "../context/ThemeContext";
import dashboardOverview from "../assets/dashboard-overview.jpeg";
import dashboardEmployeeProgress from "../assets/dashboard-employee-progress.jpeg";
// product-01 (Marriott branding) and product-02 (real-airline-styled uniform) are
// deliberately not imported here -- publishing either would imply that brand
// endorses/uses EXOPILOT, which needs their permission first. product-03/04/05 are
// generic/EXOPILOT-branded, safe to reuse across multiple spots -- each spot below
// gets its own import name (per the request that duplicated files are fine as long
// as the name isn't shared) even where the underlying file repeats.
import productHeroBanner from "../assets/product-01.jpeg";
import productVideoBackground from "../assets/product-03.jpeg";
import productWearableDevice from "../assets/product-05.jpeg";
import productHotelsBanner from "../assets/product-05.jpeg";
import productFrontDesk from "../assets/product-04.jpeg";
import productConcierge from "../assets/product-02.jpeg";
import productFnbDining from "../assets/product-01.jpeg";

const STEP_ICONS = [Mic, Server, Activity];
const FEATURE_ICONS = [FileText, Radio, Lock, ShieldCheck];

function ArchitectureDiagram() {
  const { t } = useLanguage();
  const [active, setActive] = useState(null);
  const nodes = t.privacySection.nodes;

  const NodeButton = ({ id, icon: Icon, tone }) => {
    const n = nodes[id];
    const isActive = active === id;
    return (
      <button type="button" onClick={() => setActive(isActive ? null : id)} data-testid={`diagram-node-${id}`}
        className={`w-full text-left rtl:text-right flex items-center gap-2.5 sm:gap-3 rounded-lg px-3.5 sm:px-4 py-3 sm:py-3.5 transition-colors border ${
          tone === "dark"
            ? `bg-[#111] dark:bg-neutral-950 text-white border-transparent ${isActive ? "ring-2 ring-white/40" : ""}`
            : `bg-neutral-50 dark:bg-neutral-800 border-neutral-200 dark:border-neutral-700 ${isActive ? "ring-2 ring-[#111] dark:ring-white" : "hover:border-neutral-300 dark:hover:border-neutral-600"}`
        }`}
      >
        <Icon className={`w-4 h-4 shrink-0 ${tone === "dark" ? "text-neutral-400" : "text-neutral-600 dark:text-neutral-300"}`} />
        <div className="min-w-0">
          <p className="text-sm font-medium truncate">{n.label}</p>
          <p className={`text-[11px] font-mono-data truncate ${tone === "dark" ? "text-neutral-400" : "text-neutral-400 dark:text-neutral-500"}`}>{n.detail}</p>
        </div>
      </button>
    );
  };

  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-xl p-5 sm:p-7 shadow-sm sm:shadow-md" data-testid="privacy-diagram">
      <NodeButton id="cloud" icon={Cloud} tone="light" />
      <div className="flex flex-row items-center justify-center gap-4 sm:gap-10 py-3 sm:py-4 text-[11px] font-mono-data">
        <span className="inline-flex items-center gap-1.5 text-neutral-500 dark:text-neutral-400 whitespace-nowrap"><ArrowDown className="w-3.5 h-3.5 shrink-0 rtl:rotate-180" /> {t.privacySection.arrows.sops}</span>
        <span className="inline-flex items-center gap-1.5 text-green-700 dark:text-green-500 whitespace-nowrap"><ArrowUp className="w-3.5 h-3.5 shrink-0 rtl:rotate-180" /> {t.privacySection.arrows.scores}</span>
      </div>
      <div className="border-t-2 border-dashed border-neutral-300 dark:border-neutral-700 relative my-1">
        <span className="absolute left-1/2 -translate-x-1/2 -top-2.5 bg-white dark:bg-neutral-900 px-2 sm:px-3 text-[10px] uppercase tracking-[0.18em] text-neutral-400 whitespace-nowrap">{t.privacySection.premisesBoundary}</span>
      </div>
      <p className="text-center text-[11px] text-red-600 dark:text-red-400 font-mono-data pt-3 pb-4">{t.privacySection.voiceNeverCrosses}</p>
      <NodeButton id="server" icon={Server} tone="dark" />
      <div className="flex items-center justify-center py-3 text-[11px] font-mono-data text-neutral-500 dark:text-neutral-400 text-center">
        <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><ArrowUp className="w-3.5 h-3.5 shrink-0 rtl:rotate-180" /> {t.privacySection.arrows.recordings}</span>
      </div>
      <NodeButton id="device" icon={Smartphone} tone="light" />

      <div className="mt-4 min-h-[4.5rem]">
        {active ? (
          <div data-testid="diagram-detail" className="bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 rounded-md px-4 py-3 space-y-1.5 fade-up">
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400"><span className="font-medium text-neutral-700 dark:text-neutral-200">Sends:</span> {nodes[active].sends}</p>
            <p className="text-[11px] text-neutral-500 dark:text-neutral-400"><span className="font-medium text-neutral-700 dark:text-neutral-200">Receives:</span> {nodes[active].receives}</p>
          </div>
        ) : (
          <p className="text-center text-[11px] text-neutral-400 dark:text-neutral-500 pt-2">{t.privacySection.diagramHint}</p>
        )}
      </div>
    </div>
  );
}

function ConversationReplay() {
  const { t } = useLanguage();
  const [activeStep, setActiveStep] = useState(0);
  const steps = t.replay.steps;
  const stepRefs = useRef([]);
  const scoreTone = (s) => (s >= 85 ? "text-green-700 dark:text-green-500" : s >= 70 ? "text-amber-600 dark:text-amber-400" : "text-red-600 dark:text-red-400");

  useEffect(() => {
    // Auto-advances the active step as the page scrolls -- whichever step row is
    // nearest the vertical center of the viewport becomes active, without needing
    // a click. rootMargin shrinks the observed area to a thin band around center
    // (-40% top/bottom), so a step activates right as it crosses the middle.
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries.filter((e) => e.isIntersecting);
        if (visible.length === 0) return;
        const nearest = visible.reduce((a, b) => (Math.abs(a.boundingClientRect.top) < Math.abs(b.boundingClientRect.top) ? a : b));
        const idx = stepRefs.current.indexOf(nearest.target);
        if (idx !== -1) setActiveStep(idx);
      },
      { rootMargin: "-40% 0px -40% 0px", threshold: 0 }
    );
    stepRefs.current.forEach((el) => el && observer.observe(el));
    return () => observer.disconnect();
  }, [steps.length]);

  return (
    <div className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden" data-testid="conversation-replay">
      <div className="px-6 py-4 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-sm font-medium">
          <Play className="w-4 h-4" fill="currentColor" /> {t.replay.playerLabel}
        </span>
        <span className="text-[11px] text-neutral-400 font-mono-data">03:41</span>
      </div>
      <div className="h-1 bg-neutral-100 dark:bg-neutral-800 relative">
        <div className="h-full bg-[#111] dark:bg-white" style={{ width: `${((activeStep + 1) / steps.length) * 100}%` }} />
      </div>
      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {steps.map((s, i) => (
          <button key={i} ref={(el) => { stepRefs.current[i] = el; }} onClick={() => setActiveStep(i)} data-testid={`replay-step-${i}`}
            className={`w-full text-left rtl:text-right px-6 py-4 flex items-start gap-4 transition-colors ${activeStep === i ? "bg-neutral-50 dark:bg-neutral-800" : "hover:bg-neutral-50/60 dark:hover:bg-neutral-800/60"}`}>
            <span className="text-[11px] font-mono-data text-neutral-400 mt-0.5 shrink-0 w-10">{s.time}</span>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between gap-3">
                <p className="text-sm font-medium">{s.label}</p>
                <span className={`font-heading text-lg font-light shrink-0 ${scoreTone(s.score)}`}>{s.score}</span>
              </div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-1">{s.note}</p>
            </div>
          </button>
        ))}
      </div>
      <p className="px-6 py-3 text-[11px] text-neutral-400 dark:text-neutral-500 border-t border-neutral-100 dark:border-neutral-800">{t.replay.disclaimer}</p>
    </div>
  );
}

function LandingInner() {
  const { t, dir } = useLanguage();
  const { openDemo } = useBookDemo();
  useApplyTheme();
  const [contact, setContact] = useState({ name: "", email: "", message: "" });
  const [sendingContact, setSendingContact] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const setContactField = (k) => (e) => setContact((c) => ({ ...c, [k]: e.target.value }));

  const navLinks = [
    { href: "#how", label: t.nav.how },
    { href: "#platform", label: t.nav.platform },
    { href: "#privacy", label: t.nav.privacy },
    { to: "/trust", label: t.nav.trust },
    { href: "#contact", label: t.nav.contact },
  ];

  const submitContact = (e) => {
    e.preventDefault();
    setSendingContact(true);
    toast.success(t.contact.toastTitle, { description: t.contact.toastDesc });
    setContact({ name: "", email: "", message: "" });
    setSendingContact(false);
  };

  return (
    <div className="min-h-screen bg-[#f9f9f8] dark:bg-neutral-950 text-[#111] dark:text-neutral-100" dir={dir}>
      <header className="fixed top-0 inset-x-0 z-50 bg-[#f9f9f8]/85 dark:bg-neutral-950/85 backdrop-blur-xl border-b border-neutral-200 dark:border-neutral-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
          <Link to="/" data-testid="nav-logo" className="shrink-0"><Logo /></Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-neutral-600 dark:text-neutral-300">
            {navLinks.map((l) => l.to ? (
              <Link key={l.label} to={l.to} data-testid="nav-trust-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{l.label}</Link>
            ) : (
              <a key={l.label} href={l.href} className="hover:text-[#111] dark:hover:text-white transition-colors">{l.label}</a>
            ))}
          </nav>
          <div className="flex items-center gap-1 sm:gap-3">
            {/* Language/theme controls move into the mobile menu below ~640px --
                Logo + these + the Get Started pill + hamburger don't all fit in one
                row on a 320-360px phone otherwise. */}
            <div className="hidden sm:flex items-center gap-1.5 sm:gap-3">
              <LanguageSelector compact />
              <ThemeToggle />
            </div>
            <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline text-sm text-neutral-600 dark:text-neutral-300 hover:text-[#111] dark:hover:text-white transition-colors px-3 py-2">{t.nav.signIn}</Link>
            <Link to="/register" data-testid="nav-register-link" className="text-sm bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-3.5 sm:px-5 py-2 hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors whitespace-nowrap">{t.nav.getStarted}</Link>
            <button
              data-testid="mobile-nav-toggle"
              onClick={() => setMobileNavOpen((o) => !o)}
              aria-label={mobileNavOpen ? "Close menu" : "Open menu"}
              className="md:hidden p-2 -mr-2 rtl:mr-0 rtl:-ml-2 rounded-md text-neutral-600 dark:text-neutral-300 hover:bg-neutral-100 dark:hover:bg-neutral-800 transition-colors shrink-0"
            >
              {mobileNavOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {mobileNavOpen && (
          <nav data-testid="mobile-nav-menu" className="md:hidden border-t border-neutral-200 dark:border-neutral-800 bg-[#f9f9f8] dark:bg-neutral-950 px-4 py-3 flex flex-col">
            {navLinks.map((l) => l.to ? (
              <Link key={l.label} to={l.to} onClick={() => setMobileNavOpen(false)} className="py-2.5 text-sm text-neutral-700 dark:text-neutral-200">{l.label}</Link>
            ) : (
              <a key={l.label} href={l.href} onClick={() => setMobileNavOpen(false)} className="py-2.5 text-sm text-neutral-700 dark:text-neutral-200">{l.label}</a>
            ))}
            <Link to="/login" onClick={() => setMobileNavOpen(false)} className="py-2.5 text-sm text-neutral-700 dark:text-neutral-200 border-t border-neutral-100 dark:border-neutral-800 mt-1 pt-3.5">{t.nav.signIn}</Link>
            <div className="sm:hidden flex items-center gap-3 border-t border-neutral-100 dark:border-neutral-800 mt-1 pt-3.5">
              <LanguageSelector />
              <ThemeToggle />
            </div>
          </nav>
        )}
      </header>

      <section className="relative pt-32 sm:pt-40 pb-24 px-6 overflow-hidden">
        <div className="pointer-events-none absolute -top-40 right-[-160px] w-[620px] h-[620px] rounded-full aurora-a" aria-hidden="true" />
        <div className="pointer-events-none absolute top-72 left-[-220px] w-[540px] h-[540px] rounded-full aurora-b" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="font-mono-data text-[11px] sm:text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-6">{t.hero.eyebrow}</p>
            <h1 className="font-heading font-light text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05]">
              {t.hero.titleLine}
              <span className="font-serif-accent italic text-neutral-500 dark:text-neutral-400"> {t.hero.titleAccent}</span>
            </h1>
            <p className="mt-8 text-base md:text-lg text-neutral-600 dark:text-neutral-400 max-w-xl leading-relaxed">{t.hero.subtitle}</p>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-4 flex-wrap">
              <Link to="/register" data-testid="hero-cta" className="inline-flex items-center justify-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors whitespace-nowrap w-full sm:w-auto">
                {t.hero.ctaConsole} <ArrowRight className="w-4 h-4 shrink-0 rtl:rotate-180" />
              </Link>
              <button onClick={openDemo} data-testid="hero-book-demo-cta"
                className="inline-flex items-center justify-center gap-2 border border-neutral-300 dark:border-neutral-700 rounded-full px-7 py-3.5 text-sm font-medium hover:bg-white dark:hover:bg-neutral-900 transition-colors whitespace-nowrap w-full sm:w-auto">
                <CalendarClock className="w-4 h-4 shrink-0" /> {t.hero.ctaBookDemo}
              </button>
              <a href="#how" className="text-sm text-neutral-600 dark:text-neutral-400 hover:text-[#111] dark:hover:text-white transition-colors border-b border-neutral-300 dark:border-neutral-700 pb-0.5 self-center">{t.hero.ctaArchitecture}</a>
            </div>
            <div className="mt-12 grid grid-cols-2 gap-2 sm:flex sm:flex-wrap" data-testid="trust-chips">
              {t.trustChips.map((c) => (
                <span key={c} className="inline-flex items-center justify-center gap-1.5 text-center text-xs text-neutral-600 dark:text-neutral-300 bg-white/70 dark:bg-neutral-900/70 backdrop-blur-xl border border-neutral-200 dark:border-neutral-800 rounded-full px-3.5 py-1.5">
                  <ShieldCheck className="w-3.5 h-3.5 text-neutral-500 dark:text-neutral-400 shrink-0" /> {c}
                </span>
              ))}
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-5 relative fade-up fade-up-2" data-testid="hero-preview-card">
            <div className="relative pt-6 pr-6 rtl:pr-0 rtl:pl-6">
              <div className="absolute top-0 right-0 rtl:right-auto rtl:left-0 w-[88%] rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 shadow-lg rotate-2 opacity-90">
                <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                  <span className="w-2 h-2 rounded-full bg-red-300" />
                  <span className="w-2 h-2 rounded-full bg-amber-300" />
                  <span className="w-2 h-2 rounded-full bg-green-300" />
                </div>
                <img src={dashboardOverview} alt="EXOPILOT console overview dashboard" className="w-full h-auto block aspect-[16/10] object-cover object-top" />
              </div>
              <div className="float-slow relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 shadow-2xl bg-white dark:bg-neutral-900 -rotate-1">
                <div className="flex items-center gap-1.5 px-3.5 py-2.5 bg-neutral-100 dark:bg-neutral-800 border-b border-neutral-200 dark:border-neutral-700">
                  <span className="w-2 h-2 rounded-full bg-red-400" />
                  <span className="w-2 h-2 rounded-full bg-amber-400" />
                  <span className="w-2 h-2 rounded-full bg-green-400" />
                  <span className="ml-2 text-[10px] text-neutral-400 font-mono-data">console.exopilot.io</span>
                </div>
                <img src={dashboardEmployeeProgress} alt="EXOPILOT employee score trend dashboard" className="w-full h-auto block aspect-[16/10] object-cover object-top" />
              </div>
              {/* <div className="absolute -bottom-5 -left-2 rtl:left-auto rtl:-right-2 bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-full shadow-lg pl-2 pr-3.5 py-2 flex items-center gap-2">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-500 opacity-60" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-green-600" />
                </span>
                <span className="text-xs font-medium text-neutral-700 dark:text-neutral-200 whitespace-nowrap">{t.hero.liveFloorView}</span>
              </div> */}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16">
        <span className="sm:hidden block text-center text-xs text-neutral-500 dark:text-neutral-400 whitespace-nowrap mb-3">{t.industriesLabel}</span>
        <div className="max-w-6xl mx-auto border-y border-neutral-200 dark:border-neutral-800 py-5 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-8" data-testid="industries-marquee">
          <span className="hidden sm:inline text-xs text-neutral-500 dark:text-neutral-400 shrink-0 whitespace-nowrap">{t.industriesLabel}</span>
          <div className="relative flex-1 overflow-hidden marquee-mask w-full sm:w-auto">
            <div className="flex w-max marquee-track">
              {[...t.industries, ...t.industries].map((it, i) => (
                <span key={`${it}-${i}`} className="text-xs uppercase tracking-[0.18em] text-neutral-400 whitespace-nowrap pr-16">{it}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto fade-up fade-up-1">
          <div className="relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]">
            <img src={productHeroBanner} alt="EXOPILOT pendant in use on the service floor" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-black/40" />
            <div className="absolute bottom-4 left-4 right-4 sm:bottom-8 sm:left-8 sm:right-8 grid grid-cols-3 gap-2 sm:flex sm:flex-wrap sm:gap-6 text-white">
              {t.heroStats.map(([v, l]) => (
                <div key={l} className="bg-white/10 backdrop-blur-xl border border-white/20 rounded-lg px-2.5 py-2.5 sm:px-6 sm:py-4">
                  <div className="font-heading text-lg sm:text-3xl font-light">{v}</div>
                  <div className="text-[10px] sm:text-xs text-white/80 mt-1 leading-snug">{l}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section id="how" className="px-6 py-16 sm:py-20 lg:py-28 bg-[#111] dark:bg-black text-[#ededed]">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">{t.how.eyebrow}</p>
          <h2 className="font-heading font-light text-lg md:text-lg max-w-lg text-neutral-300">{t.how.heading}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800 border border-neutral-800 rounded-lg overflow-hidden mt-14">
            {t.how.steps.map((s, i) => {
              const Icon = STEP_ICONS[i];
              return (
                <div key={s.n} className="bg-[#161616] p-10">
                  <div className="flex items-center justify-between mb-10">
                    <Icon className="w-5 h-5 text-neutral-400" />
                    <span className="font-mono-data text-xs text-neutral-600">{s.n}</span>
                  </div>
                  <h3 className="font-heading text-lg font-medium mb-3">{s.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{s.text}</p>
                </div>
              );
            })}
          </div>

          <div className="relative rounded-lg overflow-hidden border border-neutral-800 mt-14 aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]" data-testid="how-it-works-video">
            <img src={productVideoBackground} alt="EXOPILOT pendant in use" className="absolute inset-0 w-full h-full object-cover opacity-60" />
            <div className="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-4 sm:gap-5 px-6">
              <button
                data-testid="play-video-button"
                onClick={() => toast(t.how.videoToast, { description: t.how.videoToastDesc })}
                aria-label={t.how.videoTitle}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-white text-[#111] flex items-center justify-center hover:scale-105 transition-transform shadow-lg">
                <Play className="w-6 h-6 sm:w-7 sm:h-7 ml-1 rtl:ml-0 rtl:mr-1" fill="currentColor" />
              </button>
              <div className="text-center">
                <p className="text-sm font-medium text-white">{t.how.videoTitle}</p>
                <p className="text-xs text-neutral-400 mt-1 font-mono-data">{t.how.videoCaption}</p>
              </div>
            </div>
            <div className="absolute top-5 left-5 rtl:left-auto rtl:right-5 inline-flex items-center gap-1.5 text-[11px] text-white/90 bg-white/10 backdrop-blur-xl border border-white/20 rounded-full px-3 py-1.5">
              <span className="w-1.5 h-1.5 rounded-full bg-red-500" /> {t.how.videoBadge}
            </div>
          </div>
          <div className="flex justify-center mt-8">
            <button onClick={openDemo} data-testid="video-book-demo-cta"
              className="inline-flex items-center gap-2 bg-white text-[#111] rounded-full px-7 py-3 text-sm font-medium hover:bg-neutral-200 transition-colors">
              <CalendarClock className="w-4 h-4" /> {t.hero.ctaBookDemo}
            </button>
          </div>
        </div>
      </section>

      <section id="platform" className="px-6 py-16 sm:py-20 lg:py-28">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.platform.eyebrow}</p>
          <h2 className="font-heading font-light text-lg max-w-lg text-neutral-600 dark:text-neutral-400">{t.platform.heading}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-14">
            {t.platform.features.map((f, i) => {
              const Icon = FEATURE_ICONS[i];
              return (
                <div key={f.title} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 hover:-translate-y-0.5 hover:shadow-lg hover:border-neutral-300 dark:hover:border-neutral-700 transition-[transform,box-shadow,border-color] duration-200">
                  <span className="w-10 h-10 rounded-md bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-[#111] dark:text-white" />
                  </span>
                  <h3 className="font-heading text-lg font-medium mb-2">{f.title}</h3>
                  <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{f.text}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-5">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.aiCoaching.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.aiCoaching.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.aiCoaching.intro}</p>
            <ul className="mt-6 space-y-3">
              {t.aiCoaching.bullets.map((b, i) => (
                <li key={i} className="flex gap-3 text-sm text-neutral-600 dark:text-neutral-300">
                  <span className="text-neutral-300 dark:text-neutral-600 shrink-0">—</span> {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="md:col-span-7 space-y-4" data-testid="ai-coaching-card">
            {t.aiCoaching.notes.map((note, i) => (
              <div key={i} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-7" data-testid={`ai-coaching-note-${i}`}>
                <div className="flex items-center justify-between pb-4 border-b border-neutral-100 dark:border-neutral-800">
                  <span className="text-xs font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">{t.aiCoaching.cardLabel}</span>
                  <span className="text-[11px] text-neutral-400 font-mono-data">{note.name}</span>
                </div>
                <p className="text-sm text-neutral-700 dark:text-neutral-200 leading-relaxed pt-4">{note.body}</p>
              </div>
            ))}
            <div className="flex items-center gap-2 text-[11px] text-neutral-400 font-mono-data">
              <Wrench className="w-3.5 h-3.5" /> {t.aiCoaching.trainingNote}
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.replay.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.replay.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.replay.intro}</p>
          </div>
          <div className="md:col-span-7">
            <ConversationReplay />
          </div>
        </div>
      </section>

      <section id="privacy" className="relative px-6 pb-16 sm:pb-20 lg:pb-28 overflow-hidden">
        <div className="pointer-events-none absolute top-10 right-[-200px] w-[500px] h-[500px] rounded-full aurora-c" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            <ArchitectureDiagram />
          </div>
          <div className="md:col-span-7 md:pl-6 rtl:md:pl-0 rtl:md:pr-6">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.privacySection.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.privacySection.heading}<br />{t.privacySection.headingAccent}</h2>
            <ul className="mt-8 space-y-4 text-sm text-neutral-600 dark:text-neutral-300">
              {t.privacySection.bullets.map((b, i) => (
                <li key={i} className="flex gap-3"><ShieldCheck className="w-4 h-4 mt-0.5 shrink-0 text-neutral-800 dark:text-neutral-200" /> {b}</li>
              ))}
            </ul>
            <Link to="/privacy" data-testid="privacy-policy-link" className="mt-8 inline-flex items-center gap-2 text-sm font-medium text-[#111] dark:text-white border-b border-neutral-400 dark:border-neutral-600 pb-0.5 hover:border-[#111] dark:hover:border-white transition-colors">
              {t.privacySection.readFull} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
            <div className="mt-10">
              <img src={productWearableDevice} alt="EXOPILOT wearable device" className="rounded-lg border border-neutral-200 dark:border-neutral-800 object-cover w-full aspect-[3/2]" />
            </div>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28" id="trust">
        <div className="max-w-6xl mx-auto bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-9 md:p-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-8" data-testid="trust-center-teaser">
          <div className="flex items-start gap-5">
            <span className="w-12 h-12 rounded-lg bg-neutral-50 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-700 flex items-center justify-center shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#111] dark:text-white" />
            </span>
            <div>
              <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-2">{t.trustCenterTeaser.eyebrow}</p>
              <h2 className="font-heading font-light text-2xl tracking-tight">{t.trustCenterTeaser.heading}</h2>
              <p className="text-sm text-neutral-500 dark:text-neutral-400 mt-2 max-w-lg leading-relaxed">{t.trustCenterTeaser.text}</p>
            </div>
          </div>
          <Link to="/trust" data-testid="trust-center-link" className="shrink-0 inline-flex items-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-6 py-3 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors whitespace-nowrap">
            {t.trustCenterTeaser.cta} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
          </Link>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-px bg-neutral-200 dark:bg-neutral-800 border border-neutral-200 dark:border-neutral-800 rounded-lg overflow-hidden" data-testid="trust-stats">
          {t.trustStats.map(([v, l]) => (
            <div key={l} className="bg-white dark:bg-neutral-900 p-8">
              <div className="font-heading text-4xl font-light">{v}</div>
              <p className="text-xs text-neutral-500 dark:text-neutral-400 mt-2 leading-relaxed">{l}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4 text-center">{t.whereWeOperate.eyebrow}</p>
          <h2 className="font-heading font-light text-3xl tracking-tight leading-snug max-w-2xl text-center mx-auto">{t.whereWeOperate.heading}</h2>
          <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-2xl text-center mx-auto">{t.whereWeOperate.text}</p>
          <div className="mt-12">
            <IndiaBranchMap />
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.dimensions.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.dimensions.heading}<br />{t.dimensions.headingAccent}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.dimensions.intro}</p>
          </div>
          <div className="md:col-span-8 grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="dimensions-grid">
            {t.dimensions.items.map((d, i) => (
              <div key={d.name} className="group bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-6 hover:border-[#111] dark:hover:border-white transition-colors">
                <div className="flex items-baseline justify-between">
                  <h3 className="text-sm font-medium">{d.name}</h3>
                  <span className="font-heading text-2xl font-light">{d.score}</span>
                </div>
                <div className="h-1.5 bg-neutral-100 dark:bg-neutral-800 rounded-full overflow-hidden mt-3">
                  <div className="h-full bg-[#111] dark:bg-white rounded-full score-bar group-hover:bg-neutral-600 dark:group-hover:bg-neutral-300 transition-colors" style={{ width: `${d.score}%`, animationDelay: `${i * 0.12}s` }} />
                </div>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-4">{d.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.rollout.eyebrow}</p>
          <h2 className="font-heading font-light text-lg max-w-lg text-neutral-600 dark:text-neutral-400">{t.rollout.heading}</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mt-12" data-testid="rollout-timeline">
            {t.rollout.items.map((r, i) => (
              <div key={r.when} className="relative pt-8">
                <div className="absolute top-0 left-0 right-0 h-px bg-neutral-200 dark:bg-neutral-800" />
                <div className="absolute top-[-4px] left-0 rtl:left-auto rtl:right-0 w-2 h-2 rounded-full bg-[#111] dark:bg-white" />
                <p className="font-mono-data text-xs text-neutral-400">{String(i + 1).padStart(2, "0")}</p>
                <h3 className="text-sm font-medium mt-2">{r.when}</h3>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-2">{r.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="relative px-6 py-16 sm:py-20 lg:py-28 bg-[#111] dark:bg-black text-[#ededed] overflow-hidden">
        <div className="pointer-events-none absolute -bottom-40 left-1/3 w-[560px] h-[560px] rounded-full aurora-b opacity-40" aria-hidden="true" />
        <div className="relative max-w-6xl mx-auto">
          <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-4">{t.testimonials.eyebrow}</p>
          <h2 className="font-heading font-light text-lg max-w-lg text-neutral-300">{t.testimonials.heading}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14" data-testid="testimonials-grid">
            {t.testimonials.items.map((tm) => (
              <figure key={tm.name} className="bg-[#161616] border border-neutral-800 rounded-lg p-8 flex flex-col justify-between hover:-translate-y-0.5 transition-transform duration-200">
                <blockquote className="font-serif-accent italic text-lg leading-relaxed text-neutral-200">"{tm.quote}"</blockquote>
                <figcaption className="mt-8 pt-5 border-t border-neutral-800">
                  <p className="text-sm font-medium text-white">{tm.name}</p>
                  <p className="text-xs text-neutral-500 mt-1">{tm.role}</p>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section className="px-6 py-16 sm:py-20 lg:py-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.faq.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.faq.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.faq.introPrefix}<a href="mailto:privacy@exopilot.io" className="text-[#111] dark:text-white underline underline-offset-4">{t.faq.writeToUs}</a>{t.faq.orRead}<Link to="/privacy" className="text-[#111] dark:text-white underline underline-offset-4">{t.faq.fullPolicy}</Link>.</p>
          </div>
          <div className="md:col-span-8">
            <Accordion type="single" collapsible className="w-full" data-testid="faq-accordion">
              {t.faq.items.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-neutral-200 dark:border-neutral-800">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="text-left rtl:text-right text-sm font-medium hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28" id="hotels" data-testid="hotels-use-case">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-lg overflow-hidden border border-neutral-200 dark:border-neutral-800 mb-14 aspect-[4/5] sm:aspect-[16/9] lg:aspect-[21/9]">
            <img src={productHotelsBanner} alt="EXOPILOT pendant on a hotel lobby table" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
            <div className="absolute bottom-5 left-5 right-5 sm:bottom-8 sm:left-8 sm:right-8">
              <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-white/70 mb-2 sm:mb-3">{t.hotels.eyebrow}</p>
              <h2 className="font-heading font-light text-2xl sm:text-3xl lg:text-4xl tracking-tight text-white max-w-2xl leading-snug">{t.hotels.heading}</h2>
            </div>
          </div>

          <p className="text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed max-w-3xl">{t.hotels.intro}</p>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10" data-testid="hotels-use-case-cards">
            {t.hotels.cards.map((c, i) => (
              <div key={c.title} className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-7">
                {i === 0 && <img src={productFrontDesk} alt="" className="w-full aspect-[3/2] object-cover rounded-md mb-5" />}
                {i === 1 && <img src={productConcierge} alt="" className="w-full aspect-[3/2] object-cover rounded-md mb-5" />}
                {i === 2 && <img src={productFnbDining} alt="" className="w-full aspect-[3/2] object-cover rounded-md mb-5" />}
                <h3 className="text-sm font-medium">{c.title}</h3>
                <p className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed mt-2">{c.text}</p>
              </div>
            ))}
          </div>

          <div className="mt-14 bg-[#111] dark:bg-black text-white rounded-lg p-9 md:p-12">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 mb-8">{t.hotels.valueLabel}</p>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
              {t.hotels.values.map((v) => (
                <div key={v.text}>
                  <div className="font-heading text-4xl font-light">{v.stat}</div>
                  <p className="text-sm text-neutral-400 leading-relaxed mt-3">{v.text}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex justify-center mt-10">
            <button onClick={openDemo} data-testid="hotels-book-demo-cta"
              className="inline-flex items-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors">
              {t.hotels.cta} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </button>
          </div>
        </div>
      </section>

      <section className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-lg p-9 md:p-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <h2 className="font-heading font-light text-3xl md:text-4xl tracking-tight">{t.finalCta.heading}</h2>
            <p className="text-neutral-400 dark:text-neutral-500 text-sm mt-3">{t.finalCta.text}</p>
          </div>
          <div className="shrink-0 flex flex-col sm:flex-row gap-3">
            <Link to="/register" data-testid="footer-cta" className="inline-flex items-center justify-center gap-2 bg-white dark:bg-[#111] text-[#111] dark:text-white rounded-full px-7 py-3.5 text-sm font-medium hover:bg-neutral-200 dark:hover:bg-neutral-800 transition-colors whitespace-nowrap">
              {t.finalCta.ctaGetStarted} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
            </Link>
            <button onClick={openDemo} data-testid="final-book-demo-cta"
              className="inline-flex items-center justify-center gap-2 border border-white/30 dark:border-neutral-300 text-white dark:text-[#111] rounded-full px-7 py-3.5 text-sm font-medium hover:bg-white/10 dark:hover:bg-neutral-100 transition-colors whitespace-nowrap">
              <CalendarClock className="w-4 h-4" /> {t.finalCta.ctaBookDemo}
            </button>
          </div>
        </div>
      </section>

      <section id="contact" className="px-6 pb-16 sm:pb-20 lg:pb-28">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-4">
            <p className="font-mono-data text-xs uppercase tracking-[0.2em] text-neutral-500 dark:text-neutral-400 mb-4">{t.contact.eyebrow}</p>
            <h2 className="font-heading font-light text-3xl tracking-tight leading-snug">{t.contact.heading}</h2>
            <p className="mt-5 text-sm text-neutral-500 dark:text-neutral-400 leading-relaxed">{t.contact.text}<a href="mailto:hello@exopilot.io" className="text-[#111] dark:text-white underline underline-offset-4">hello@exopilot.io</a>.</p>
          </div>
          <div className="md:col-span-8">
            <form onSubmit={submitContact} data-testid="contact-form" className="bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 rounded-lg p-8 space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                <div>
                  <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">{t.contact.nameLabel}</label>
                  <input data-testid="contact-name-input" required value={contact.name} onChange={setContactField("name")} placeholder={t.contact.namePlaceholder}
                    className="mt-1.5 w-full bg-[#f9f9f8] dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] dark:focus:ring-white focus:border-transparent" />
                </div>
                <div>
                  <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">{t.contact.emailLabel}</label>
                  <input data-testid="contact-email-input" type="email" required value={contact.email} onChange={setContactField("email")} placeholder={t.contact.emailPlaceholder}
                    className="mt-1.5 w-full bg-[#f9f9f8] dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700 rounded-md px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#111] dark:focus:ring-white focus:border-transparent" />
                </div>
              </div>
              <div>
                <label className="text-xs font-medium text-neutral-600 dark:text-neutral-400 uppercase tracking-wide">{t.contact.messageLabel}</label>
                <textarea data-testid="contact-message-input" required rows={4} value={contact.message} onChange={setContactField("message")} placeholder={t.contact.messagePlaceholder}
                  className="mt-1.5 w-full bg-[#f9f9f8] dark:bg-neutral-950 border border-neutral-300 dark:border-neutral-700 rounded-md px-3.5 py-2.5 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-[#111] dark:focus:ring-white focus:border-transparent resize-none" />
              </div>
              <button type="submit" data-testid="contact-submit-button" disabled={sendingContact}
                className="inline-flex items-center gap-2 bg-[#111] dark:bg-white text-white dark:text-[#111] rounded-full px-7 py-3 text-sm font-medium hover:bg-neutral-800 dark:hover:bg-neutral-200 transition-colors disabled:opacity-60">
                {sendingContact ? t.contact.sending : t.contact.send} <ArrowRight className="w-4 h-4 rtl:rotate-180" />
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="border-t border-neutral-200 dark:border-neutral-800 px-6 py-12">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <Logo />
          <nav className="flex flex-wrap gap-4 sm:gap-6 text-sm text-neutral-500 dark:text-neutral-400">
            <a href="#how" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.howItWorks}</a>
            <a href="#platform" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.platform}</a>
            <a href="#contact" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.contact}</a>
            <Link to="/trust" data-testid="footer-trust-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.trust}</Link>
            <Link to="/privacy" data-testid="footer-privacy-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.privacy}</Link>
            <Link to="/cookies" data-testid="footer-cookies-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.cookies}</Link>
            <Link to="/terms" data-testid="footer-terms-link" className="hover:text-[#111] dark:hover:text-white transition-colors">{t.footer.terms}</Link>
          </nav>
          <p className="text-xs text-neutral-400 dark:text-neutral-500">{t.footer.tagline}</p>
        </div>
      </footer>

      <StickyBookDemo />
      <ChatWidget />
      <BookDemoModal />
    </div>
  );
}

export default function Landing() {
  return (
    <BookDemoProvider>
      <LandingInner />
    </BookDemoProvider>
  );
}
